/**
 * AI Skills — user-authored, reusable capability packs that teach the on-device AI Workflows agent
 * how to do a domain task well. A skill is a directory containing a `SKILL.md` (YAML frontmatter +
 * markdown instructions) and optional `scripts/`, `references/`, and `assets/` subfolders, per the
 * Agent Skills specification (https://agentskills.io/specification) that LangChain deepagents parses.
 *
 * These models live in `shared/` so both the desktop renderer (Extensions manager, composer picker)
 * and the desktop worker/agent can use them. Intentionally free of any runtime dependency on
 * `deepagents` — the limit constants below mirror the spec so shared/web/admin bundles stay light.
 */

/** Where a skill came from. Only `user`/`imported` are producible in v1; the rest are future-proofing. */
export type SkillSource = 'user' | 'imported' | 'organization' | 'marketplace';

/**
 * Who a skill is shared with. Only `private` is active in v1; `organization`/`public` are reserved for
 * the future org-sharing + marketplace flows (see shared/api/skills). Persisted so the surface exists.
 */
export type SkillVisibility = 'private' | 'organization' | 'public';

/**
 * How an enabled skill is chosen for a run:
 * - `auto` — the assistant may pick it automatically when the prompt matches, AND the user can attach it
 *   explicitly via `/`/`@`.
 * - `manual` — the assistant never picks it on its own; it's only used when the user attaches it via
 *   `/`/`@` or the context adder.
 * Only meaningful when the skill is `enabled`.
 */
export type SkillActivation = 'auto' | 'manual';

/**
 * What a skill starts as however it was added — authored, imported, or installed from an org/marketplace.
 * Manual for now: auto-selection injects matched skills' metadata into a small on-device model's window, and
 * we need better token/memory management before dynamic runtime selection can be trusted to scale with a
 * growing library. Users opt individual skills into `auto`.
 */
export const DEFAULT_SKILL_ACTIVATION: SkillActivation = 'manual';

/**
 * Skill name (and its parent directory name) constraints, per the Agent Skills spec. Mirror of
 * deepagents' MAX_SKILL_NAME_LENGTH / MAX_SKILL_DESCRIPTION_LENGTH so our validation matches the
 * runtime loader exactly without importing the heavy package into shared code.
 */
export const MAX_SKILL_NAME_LENGTH = 64;
export const MAX_SKILL_DESCRIPTION_LENGTH = 1024;
/** Max size of any single file inside a skill directory (10 MB), mirrors deepagents' MAX_SKILL_FILE_SIZE. */
export const MAX_SKILL_FILE_SIZE = 10 * 1024 * 1024;

/**
 * A single installed skill, as surfaced to the renderer and threaded to the worker. `dirPath` is the
 * absolute on-disk directory (…/userData/skills/<name>); `name` always equals that directory's name.
 */
export interface SkillModel {
    /** Stable id (equals the directory name in v1; kept distinct so a future rename can preserve it). */
    id: string;
    /** Directory name / frontmatter `name`: lowercase alphanumeric + hyphens, 1–64 chars, no leading/trailing/`--`. */
    name: string;
    /** Frontmatter `description`: what the skill does AND when to use it (≤ 1024 chars). Drives selection. */
    description: string;
    /** Extra lowercase keywords to widen prompt-based matching (brand names, aliases). Not part of the spec. */
    keywords?: string[];
    /** Absolute path to the skill directory on disk. */
    dirPath: string;
    /** Absolute path to the skill's SKILL.md. */
    skillMdPath: string;
    source: SkillSource;
    /** When false, the skill is hidden from selection and the picker (installed but paused). */
    enabled: boolean;
    /** Auto-detected vs. manual-only (see {@link SkillActivation}). Only meaningful when `enabled`. */
    activation: SkillActivation;
    /** True when the skill ships one or more scripts (files under `scripts/`) the agent can run. */
    hasScripts: boolean;
    /** True when the skill ships reference/asset files (under `references/` or `assets/`) the agent can read. */
    hasReferences: boolean;
    /** v1: always `private`. Reserved for org sharing / marketplace. */
    visibility: SkillVisibility;
    /** Third-party publisher name for imported/marketplace skills; omitted for user-authored ones. */
    publisher?: string;
    createdAt: number;
    updatedAt: number;

    // --- server sync (present on skills published to / installed from the API) ---
    /** Server id of this skill's API registration (the `AgentSkillModel.id`). */
    remoteId?: string;
    /** Server `packageVersion` captured at the last publish/install; a higher server value = update available. */
    remoteVersion?: number;
    /** Server `packageChecksum` captured at the last publish/install, to confirm real package changes. */
    remoteChecksum?: string;
    /** Organization this skill is scoped to on the server, if any. */
    organizationId?: string;
    /** True when this is official Cephable-managed content. */
    isOfficial?: boolean;
}

/** Payload for creating a new skill from the guided builder. */
export interface CreateSkillInput {
    name: string;
    description: string;
    keywords?: string[];
    /** Full markdown body for SKILL.md (below the frontmatter). When omitted, a template body is used. */
    body?: string;
    /** Auto vs manual activation (defaults to {@link DEFAULT_SKILL_ACTIVATION}). */
    activation?: SkillActivation;
}

/** Fields the editor can change on an existing skill. `name` changes rename the directory. */
export interface UpdateSkillInput {
    id: string;
    name?: string;
    description?: string;
    keywords?: string[];
    body?: string;
    enabled?: boolean;
    activation?: SkillActivation;
}

/**
 * Server-sync metadata stamped onto a skill when it is published to / installed from the API. Used to
 * reconcile the local on-disk copy with its server registration and detect available package updates.
 */
export interface RemoteSkillMeta {
    remoteId: string;
    remoteVersion?: number;
    remoteChecksum?: string;
    organizationId?: string;
    isOfficial?: boolean;
    /** How the local copy should be tagged (e.g. `organization` / `marketplace`). */
    source?: SkillSource;
    visibility?: SkillVisibility;
}

/** Result of validating a skill name/description against the spec, for inline form feedback. */
export interface SkillValidationResult {
    valid: boolean;
    /** i18n-key-free, human-readable reason when invalid (surfaced under the field). */
    error?: string;
}

/**
 * A supporting file that lives inside a skill directory alongside its SKILL.md. `kind` maps to the
 * subfolder it's stored in per the Agent Skills spec: `reference` → `references/`, `script` → `scripts/`,
 * `asset` → `assets/`.
 */
export type SkillFileKind = 'reference' | 'script' | 'asset';

export interface SkillFile {
    /** Path relative to the skill directory, POSIX-style (e.g. `references/api.md`). */
    relPath: string;
    /** File name (basename). */
    name: string;
    kind: SkillFileKind;
    /** Size in bytes. */
    size: number;
}
